import { useState, useEffect } from "react";
import { ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import {
  Box,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "react-pro-sidebar/dist/css/styles.css";
import { tokens } from "../theme";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import GetAppOutlinedIcon from "@mui/icons-material/GetAppOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { useAuth } from "../context/AuthContext";

const Sidebar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [selected, setSelected] = useState("Home");
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(isSmallScreen);
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();

  useEffect(() => {
    const disableRightClick = (e) => e.preventDefault();
    document.addEventListener("contextmenu", disableRightClick);

    return () => {
      document.removeEventListener("contextmenu", disableRightClick);
    };
  }, []);

  useEffect(() => {
    const pathToMenuItem = {
      "/home": "Home",
      "/deposit": "Deposit",
      "/withdraw": "Withdraw",
      "/analytics": "Analytics",
      "/premium": "Premium",
      "/contactus": "ContactUs",
    };
    const currentMenuItem = pathToMenuItem[location.pathname] || "Home";
    setSelected(currentMenuItem);
  }, [location.pathname]);

  useEffect(() => {
    setIsCollapsed(isSmallScreen);
  }, [isSmallScreen]);

  const handleToggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleLogout = () => {
    signOut();
    navigate("/signin");
  };

  const handleLogoutClick = () => {
    setOpenLogoutDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenLogoutDialog(false);
  };

  return (
    <Box
      sx={{
        position: "relative",
        zIndex: 30,
        "& .pro-sidebar-inner": {
          background: "teal !important",
          zIndex: 30,
        },
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        "& .pro-inner-item": {
          padding: "5px 35px 5px 20px !important",
        },
        "& .pro-inner-item:hover": {
          color: "#FFFFFF !important",
        },
        "& .pro-menu-item.active": {
          color: "#FFFFFF !important",
        },
      }}
    >
      <ProSidebar collapsed={isCollapsed}>
        <Menu iconShape="square">
          {/* Sidebar header with toggle icon */}
          <MenuItem
            icon={<MenuOutlinedIcon />}
            onClick={handleToggleSidebar}
            style={{
              margin: "10px 0 20px 0",
              color: colors.grey[100],
            }}
          >
            {!isCollapsed && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ml="5px"
              >
                <Typography variant="h4" color={colors.grey[100]}>
                  Money8gg
                </Typography>
                <IconButton onClick={handleToggleSidebar}>
                  <MenuOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          {/* Sidebar menu items */}
          <MenuItem
            active={selected === "Home"}
            icon={<HomeOutlinedIcon />}
            style={{ color: colors.grey[100] }}
            onClick={() => setSelected("Home")}
          >
            <Link
              to="/home"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <Typography>Home</Typography>
            </Link>
          </MenuItem>

          <MenuItem
            active={selected === "Deposit"}
            icon={<GetAppOutlinedIcon />}
            style={{ color: colors.grey[100] }}
            onClick={() => setSelected("Deposit")}
          >
            <Link
              to="/deposit"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <Typography>Deposit</Typography>
            </Link>
          </MenuItem>

          <MenuItem
            active={selected === "Withdraw"}
            icon={<PublishOutlinedIcon />}
            style={{ color: colors.grey[100] }}
            onClick={() => setSelected("Withdraw")}
          >
            <Link
              to="/withdraw"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <Typography>Withdraw</Typography>
            </Link>
          </MenuItem>

          <MenuItem
            active={selected === "Analytics"}
            icon={<TrendingUpOutlinedIcon />}
            style={{ color: colors.grey[100] }}
            onClick={() => setSelected("Analytics")}
          >
            <Link
              to="/analytics"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <Typography>Analytics</Typography>
            </Link>
          </MenuItem>

          <MenuItem
            active={selected === "Premium"}
            icon={<AutoAwesomeOutlinedIcon />}
            style={{ color: colors.grey[100] }}
            onClick={() => setSelected("Premium")}
          >
            <Link
              to="/premium"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <Typography>AI</Typography>
            </Link>
          </MenuItem>

          <MenuItem
            active={selected === "ContactUs"}
            icon={<ContactsOutlinedIcon />}
            style={{ color: colors.grey[100] }}
            onClick={() => setSelected("ContactUs")}
          >
            <Link
              to="/contactus"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <Typography>Contact Us</Typography>
            </Link>
          </MenuItem>

          <MenuItem
            icon={<LogoutOutlinedIcon />}
            style={{ color: colors.grey[100] }}
            onClick={handleLogoutClick}
          >
            <Typography>Log Out</Typography>
          </MenuItem>
        </Menu>

        <Dialog open={openLogoutDialog} onClose={handleCloseDialog}>
          <DialogTitle>Confirm Logout</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to log out?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDialog}
              className="bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600 transition"
            >
              Cancel
            </Button>
            <Button
              className="bg-teal-600 text-white py-2 px-4 rounded-md hover:bg-teal-700 transition"
              onClick={handleLogout}
            >
              Log Out
            </Button>
          </DialogActions>
        </Dialog>

        <Box textAlign="center" mt="auto" p="10px" color={colors.grey[100]}>
          <Typography variant="body2">
            &copy; {new Date().getFullYear()} Money8gg
          </Typography>
        </Box>
      </ProSidebar>
    </Box>
  );
};

export default Sidebar;
